import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { PREVENTCHARACTER, PreventCharacter } from "@comunicare/comunicare-angular-forms-validator";
import { ConfirmationDialogComponent, ConfirmationDialogType } from "@comunicare/comunicare-dashboard-modals";
import { Tools } from "@comunicare/comunicare-helper";
import {
  DocCategory,
  FHIRHelper,
  IDocument,
  INewPrescription,
  IOfficialCareplanDetails,
  IPractitionerTemplate,
  IPrescription,
  IPrescriptionInCareplan,
  IReference,
  Practitioner,
  PRESCRIPTION_TYPE,
  QUALIFICATIONS,
} from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import moment from "moment";
import { combineLatest, Observable, Subject } from "rxjs";
import { first } from "rxjs/operators";
import { DocumentService } from "src/app/providers/document.service";
import { PatientRegistrationService } from "src/app/providers/patientRegistration.service";
import { PractitionerService } from "src/app/providers/practitioner.service";
import { PrescriptionService } from "src/app/providers/prescription.service";
import { SessionService } from "src/app/providers/session.service";
import { PrescriptionDevicesFormComponent } from "./prescription-devices-form/prescription-devices-form.component";
import { PrescriptionTemplateModalComponent } from "./prescription-template-modal/prescription-template-modal.component";
@Component({
  selector: "app-presciption-form",
  templateUrl: "./prescription-form.component.html",
  styleUrls: ["./prescription-form.component.scss"],
})
export class PrescriptionFormComponent implements OnInit, OnDestroy {
  private onDestroy$ = new Subject<void>();

  public isLoading = true;
  public prescriptionForm: UntypedFormGroup;
  public careplanPrescription: IPrescriptionInCareplan;
  private careplanPrescriptionIndex = 0;
  public documents: File[] = [];
  public isLastStep = true;
  public prescriptionPeriodMax = 12;
  public prescriptionPeriodTimeUnit: string;
  public renewPeriodMax = 12;
  public renewPeriodTimeUnit: string;
  public endDateCalculated: string;
  public availableCoefficients: number[] = [];
  public pricings: {
    month: number;
    code: string;
    label: string;
    amount: number;
  }[];
  public availableDoctors: Practitioner[];
  public availableNurses: Practitioner[];
  public officialCareplan: IOfficialCareplanDetails;
  public canSkip = true;
  public isReimbursement: boolean;
  public showNeedValidationCheckbox = true;
  public practitionerTemplates: IPractitionerTemplate[] = [];
  private serviceRef: string;
  public templatesSelected: IPractitionerTemplate[] = [];
  public linkedDocuments: IReference[] = [];
  public PRESCRIPTION_TYPE = PRESCRIPTION_TYPE;
  public isEndlessPrescription = false;
  public PREVENTCHARACTER = PREVENTCHARACTER;

  @ViewChild(PrescriptionDevicesFormComponent) prescriptionDeviceFormComp: PrescriptionDevicesFormComponent;

  constructor(
    private fb: UntypedFormBuilder,
    public patientRegistrationService: PatientRegistrationService,
    private prescriptionService: PrescriptionService,
    public practitionerService: PractitionerService,
    private sessionService: SessionService,
    private documentService: DocumentService,
    private dialog: MatDialog,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.calculateisLastStep();
    this.careplanPrescription = this.patientRegistrationService.careplanPrescriptions[this.careplanPrescriptionIndex];

    this.initForm();
    this.serviceRef = this.patientRegistrationService.careplan?.identifier
      ? this.patientRegistrationService.careplan.identifier.find((identifier) => identifier.system === "organization")?.use
      : this.patientRegistrationService.prescription.performer.find((p) => p.type === "HealthcareService")?.reference;
  }
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private initForm(): void {
    this.careplanPrescription = this.patientRegistrationService.careplanPrescriptions[this.careplanPrescriptionIndex];
    this.isReimbursement = [
      PRESCRIPTION_TYPE.REIMBURSEMENT_BELGIUM,
      PRESCRIPTION_TYPE.REIMBURSEMENT_FRANCE,
      PRESCRIPTION_TYPE.HOME_CARE_MOROCCO,
    ].includes(this.careplanPrescription.prescriptionType);
    this.prescriptionForm = this.fb.group({
      isRenew: new FormControl<boolean>(false, [Validators.required]),
      startDate: new FormControl<string>(undefined, Validators.required),
      prescriptionDuration: new FormControl<number>(undefined, [Validators.required]),
      renewable: new FormControl<boolean>(true, [Validators.required]),
      autoRenew: new FormControl<boolean>(undefined),
      renewDuration: new FormControl<number>(undefined),
      isCumulative: new FormControl<boolean>(undefined),
      nbMonthCumulative: new FormControl<number>(undefined),
      coefficientIncrease: new FormControl<number>(1, [Validators.required]),
      monthlyPaidAmount: new FormControl<number>({ value: 1, disabled: true }, [Validators.required]),
      chargeNurse: new FormControl<Practitioner>(undefined),
      chargeDoctor: new FormControl<Practitioner>(undefined, Validators.required),
      needValidation: new FormControl<boolean>(false),
      pricingIndex: new FormControl<number>(0),
    });
    if (this.isReimbursement) {
      this.prescriptionService
        .getPrescriptionsOfficialCareplan(this.careplanPrescription.officialCareplan)
        .pipe(first())
        .subscribe((details) => {
          this.officialCareplan = details;
          this.prescriptionPeriodMax = details.careplan.prescriptionPeriodMax ?? 12;
          this.prescriptionPeriodTimeUnit = details.careplan.prescriptionPeriodTimeUnit ?? "m";
          this.renewPeriodMax = details.careplan.renewPeriodMax ?? 12;
          this.renewPeriodTimeUnit = details.careplan.renewPeriodTimeUnit ?? "m";
          const operatorpricing = details.operatorpricings;
          this.pricings = operatorpricing.filter((p) => details.careplan.codes.includes(p.code));
          if (this.pricings.every((p) => p.amount === this.pricings[0].amount)) {
            // all amount are the same, only need to show one
            this.pricings = [this.pricings[0]];
          }
          this.availableCoefficients = this.officialCareplan.careplan.coefficients;
          this.prescriptionForm.patchValue({
            startDate: this.patientRegistrationService.directToPrescription
              ? moment().format()
              : this.patientRegistrationService.careplan.period.start,
            prescriptionDuration: this.prescriptionPeriodMax,
            renewDuration: this.renewPeriodMax,
            coefficientIncrease: Math.min(...this.availableCoefficients) ?? 1,
            monthlyPaidAmount: this.pricings[this.pricingIndex].amount * (Math.min(...this.availableCoefficients) ?? 1),
          });

          if (this.careplanPrescription.prescriptionType === PRESCRIPTION_TYPE.HOME_CARE_MOROCCO) {
            this.prescriptionPeriodMax = 12;
            this.prescriptionForm.get("prescriptionDuration").setValue(12);
            this.prescriptionForm.get("prescriptionDuration").setValidators([]);
            this.prescriptionForm.get("chargeDoctor").setValidators([]);
            this.prescriptionForm.get("renewDuration").setValue(undefined);
          }

          this.convertTimeUnitForI18n();
          this.setEndDate();
          this.prescriptionService
            .getPrescriptionTemplates(this.serviceRef)
            .pipe(first())
            .subscribe((result) => {
              this.practitionerTemplates = result;
              this.setDefaultTemplate();
              this.initAvailablePractitioner();
            });
        });
    } else {
      this.isLoading = false;
    }
  }

  private initAvailablePractitioner(): void {
    this.practitionerService
      .listForService(this.serviceRef)
      .pipe(first())
      .subscribe((practitioners) => {
        this.availableDoctors = practitioners
          .filter((p) => this.practitionerService.getRole(p) === QUALIFICATIONS.DOCTOR_CODE)
          .sort((a, b) => {
            return a?.name?.given[0]?.toLowerCase() < b?.name?.given[0]?.toLowerCase()
              ? -1
              : a?.name?.given[0]?.toLowerCase() > b?.name?.given[0]?.toLowerCase()
              ? 1
              : 0;
          });
        this.availableNurses = practitioners
          .filter((p) => this.practitionerService.getRole(p) === QUALIFICATIONS.NURSE_CODE)
          .sort((a, b) => {
            return a?.name?.given[0]?.toLowerCase() < b?.name?.given[0]?.toLowerCase()
              ? -1
              : a?.name?.given[0]?.toLowerCase() > b?.name?.given[0]?.toLowerCase()
              ? 1
              : 0;
          });
        const currentDoctor = this.availableDoctors.find((d) => d?.caremateId?.value === this.sessionService.account.caremateIdentifier);
        if (currentDoctor) {
          this.prescriptionForm.get("chargeDoctor").setValue(currentDoctor);
          this.showNeedValidationCheckbox = false;
        } else if (this.availableDoctors?.length === 1) {
          this.prescriptionForm.get("chargeDoctor").setValue(this.availableDoctors[0]);
        }
        if (this.availableNurses?.length === 1) {
          this.prescriptionForm.get("chargeNurse").setValue(this.availableNurses[0]);
        }
        this.isLoading = false;
        if (this.patientRegistrationService.prescription) {
          this.initFormWithPrescription(this.patientRegistrationService.prescription);
        }
      });
  }

  public radioButtonClicked(value: boolean, formControlName: string): void {
    const buttonTrue = document.getElementById(formControlName + "1");
    const buttonFalse = document.getElementById(formControlName + "0");
    this.prescriptionForm.get(formControlName).setValue(value);
    if (value) {
      buttonTrue.classList.add("colored");
      buttonFalse.classList.remove("colored");
    } else {
      buttonTrue.classList.remove("colored");
      buttonFalse.classList.add("colored");
    }
    if (formControlName.startsWith("isRenew")) {
      this.onIsRenewChange();
    }
  }

  public import(event: { target: { files: FileList } }): void {
    const files = event.target.files;
    if (files.length) {
      for (let i = 0; i < files.length; i++) {
        this.documents.push(files.item(i));
      }
    } else {
      throw new Error("no file selected");
    }
  }
  private calculateisLastStep(): void {
    this.isLastStep =
      this.patientRegistrationService.isLastStep &&
      this.patientRegistrationService.careplanPrescriptions.length - this.careplanPrescriptionIndex === 1;
  }
  public save(): void {
    if (!this.isReimbursement) {
      this.prescriptionDeviceFormComp.apply();
    } else if (this.prescriptionForm.valid) {
      this.isLoading = true;
      if (this.documents?.length) {
        this.uploadDocuments()
          .pipe(first())
          .subscribe((documents) => {
            this.createPrescription(documents);
          });
      } else if (this.templatesSelected?.length) {
        this.autoCompletePrescriptionTemplate()
          .pipe(first())
          .subscribe((blobs) => {
            if (blobs?.length) {
              this.documents = [];
              blobs.forEach((blob, i) => {
                // "name_completed_patientName_patientFirstname_date"
                const fileName =
                  this.templatesSelected?.[i]?.name?.substring(0, this.templatesSelected?.[i]?.name?.length - 4) +
                  "_completed_" +
                  this.patientRegistrationService.patientUser.user.name +
                  "_" +
                  this.patientRegistrationService.patientUser.user.firstname +
                  "_" +
                  moment().format("DDMMYY") +
                  ".pdf";
                this.documents.push(new File([blob], fileName, { type: "application/pdf" }));
              });
              this.templatesSelected = [];
              this.save();
            }
          });
      } else {
        // no document to upload
        this.createPrescription();
      }
    } else {
      this.prescriptionForm.markAllAsTouched();
    }
  }

  public next(): void {
    if (this.isLastStep) {
      // no more prescription
      if (this.patientRegistrationService.isLastStep) {
        this.patientRegistrationService.onFinish();
      } else {
        this.patientRegistrationService.onNextStep$.next();
      }
    } else {
      this.careplanPrescriptionIndex++;
      this.prescriptionForm.reset();
      this.calculateisLastStep();
      this.documents = [];
      this.initForm();
    }
  }
  private convertTimeUnitForI18n() {
    this.prescriptionPeriodTimeUnit = Tools.getI18nPeriodUnitKey(
      this.prescriptionPeriodTimeUnit ? this.prescriptionPeriodTimeUnit.toUpperCase() : "M" // toUpperCase needed because the backend return "m" for month (for prescription / billing)
    );
    this.renewPeriodTimeUnit = Tools.getI18nPeriodUnitKey(this.renewPeriodTimeUnit ? this.renewPeriodTimeUnit.toUpperCase() : "M");
  }

  public setEndDate(value?: number): void {
    if (this.prescriptionForm.get("startDate").value && this.prescriptionDuration && this.prescriptionPeriodTimeUnit) {
      this.endDateCalculated = moment(this.prescriptionForm.get("startDate").value)
        .add(Number(this.prescriptionDuration), this.prescriptionPeriodTimeUnit as moment.unitOfTime.DurationConstructor)
        .format();
    }
    if (Tools.isDefined(value)) {
      this.onDurationChange(value);
    }
    this.checkCanSkip();
  }

  public setMonthlyPaidAmount(): void {
    if (Tools.isDefined(this.prescriptionForm.get("coefficientIncrease").value)) {
      this.prescriptionForm
        .get("monthlyPaidAmount")
        .setValue(this.pricings[this.pricingIndex].amount * Number(this.prescriptionForm.get("coefficientIncrease").value));
    }
  }

  public onIsRenewChange(): void {
    if (this.prescriptionForm.get("isRenew").value) {
      this.prescriptionPeriodMax = this.renewPeriodMax;
    } else {
      this.prescriptionPeriodMax = this.officialCareplan.careplan.prescriptionPeriodMax;
    }
  }

  public onDurationChange(value: number): void {
    if (this.prescriptionForm.get("isRenew").value) {
      this.prescriptionForm.get("prescriptionDuration").setValue(value);
      this.prescriptionForm.get("renewDuration").setValue(value);
    }
  }

  public checkCanSkip(): void {
    const nurseValue = this.prescriptionForm.get("chargeNurse").value;
    const doctorValue = this.prescriptionForm.get("chargeDoctor").value;
    const needValidationValue = this.prescriptionForm.get("needValidation").value;
    this.canSkip =
      (nurseValue === "" || !Tools.isDefined(nurseValue)) && (doctorValue === "" || !Tools.isDefined(doctorValue)) && !needValidationValue;
  }

  private uploadDocuments(): Observable<IDocument[]> {
    const uploadSubs: Observable<IDocument>[] = [];
    this.documents?.forEach((d) => {
      const formData = new FormData();
      formData.append("service", JSON.stringify([this.serviceRef]));
      formData.append("name", d.name);
      formData.append("file", d);
      formData.append("patient", FHIRHelper.getMainIdentifier(this.patientRegistrationService.patientUser.patient).value);
      formData.append("docCategory", DocCategory.ACCEPTANCE_TREATMENT);
      formData.append("contentType", d.type);
      uploadSubs.push(this.documentService.uploadDocument(formData).pipe(first()));
    });
    return combineLatest(uploadSubs);
  }

  private autoCompletePrescriptionTemplate(): Observable<Blob[]> {
    const uploadSubs: Observable<Blob>[] = [];
    this.templatesSelected?.forEach((t) => {
      uploadSubs.push(
        this.prescriptionService.autoCompletePrescriptionTemplate(
          t.identifier,
          FHIRHelper.getMainIdentifier(this.patientRegistrationService.patientUser.patient).value,
          this.sessionService.userLang,
          this.prescriptionDuration
        )
      );
    });
    return combineLatest(uploadSubs);
  }

  private createPrescription(documents?: IDocument[]): void {
    if (this.patientRegistrationService.isPendingPrescriptionEdit) {
      this.editPrescription(documents);
    } else {
      const formValue = this.prescriptionForm.getRawValue();
      const responsibleId =
        this.careplanPrescription.prescriptionType === PRESCRIPTION_TYPE.HOME_CARE_MOROCCO
          ? FHIRHelper.getMainIdentifier(formValue.chargeDoctor)?.value ?? this.sessionService.isAdmin()
            ? this.sessionService.account.caremateIdentifier
            : undefined
          : FHIRHelper.getMainIdentifier(formValue.chargeDoctor)?.value;
      const newPrescription: INewPrescription = {
        patientId: FHIRHelper.getMainIdentifier(this.patientRegistrationService.patientUser.patient).value,
        responsibleId: responsibleId,
        responsibleNurseId: formValue.chargeNurse ? FHIRHelper.getMainIdentifier(formValue.chargeNurse).value : undefined,
        serviceId: this.serviceRef,
        careplanId: this.patientRegistrationService.careplan
          ? FHIRHelper.getMainIdentifier(this.patientRegistrationService.careplan).value
          : this.patientRegistrationService.careplanRef.reference,
        officialCareplanId:
          this.careplanPrescription.officialCareplan ?? FHIRHelper.getMainIdentifier(this.patientRegistrationService.careplan).value,
        occurence: {
          occurenceDateTime: formValue.startDate,
          occurenceTiming: formValue.prescriptionDuration
            ? {
                duration: formValue.prescriptionDuration,
                durationUnit: this.prescriptionPeriodTimeUnit.substring(0, 1),
              }
            : undefined,
          renewal: formValue.prescriptionDuration ? formValue.renewable : false,
          renewalParams: formValue.prescriptionDuration
            ? {
                duration:
                  this.careplanPrescription.prescriptionType === PRESCRIPTION_TYPE.HOME_CARE_MOROCCO
                    ? formValue.prescriptionDuration
                    : formValue.renewDuration,
                durationUnit:
                  this.careplanPrescription.prescriptionType === PRESCRIPTION_TYPE.HOME_CARE_MOROCCO
                    ? this.prescriptionPeriodTimeUnit.substring(0, 1)
                    : this.renewPeriodTimeUnit.substring(0, 1),
              }
            : undefined,
        },
        deviceOrdersIds: [],
        askApproval: formValue.needValidation,
        pricing: {
          pricingCareTeam: {
            coefficient: formValue.coefficientIncrease,
            codeVAT: this.officialCareplan.careplan.vat,
          },
          billingFrequency: formValue.isCumulative ? formValue.nbMonthCumulative : 1,
          billingFrequencyUnits: "m",
        },
        documentIds: documents?.length ? documents.map((d) => d.identifier) : undefined,
        previousPrescriptionId:
          formValue.isRenew && this.patientRegistrationService?.prescription?.identifier?.[0]?.value
            ? this.patientRegistrationService?.prescription?.identifier?.[0]?.value
            : undefined,
      };

      this.prescriptionService
        .createPrescription(newPrescription, this.careplanPrescription.prescriptionType)
        .pipe(first())
        .subscribe(
          (result) => {
            this.patientRegistrationService.onPrescriptionCreated$.next(result);
            this.next();
            this.isLoading = false;
          },
          (err) => {
            const prescription = err.additionalData as IPrescription;
            if (prescription) {
              const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
                data: {
                  message: this.translateService.instant("forms.prescription.prescriptionAlreadyExists.message"),
                  type: ConfirmationDialogType.CONFIRM,
                },
                disableClose: true,
              });
              dialogRef.afterClosed().subscribe((adaptDate) => {
                if (adaptDate) {
                  const duration = prescription.occurence.occurenceTiming?.duration;
                  const unit =
                    prescription.occurence.occurenceTiming?.durationUnit === "m" // backend "m" = month but for moment.js "m" = minutes
                      ? "M"
                      : prescription.occurence.occurenceTiming?.durationUnit ?? "M";
                  const newDate = duration
                    ? moment(prescription.occurence.occurenceDateTime)
                        .add(duration, unit as moment.unitOfTime.Base)
                        .add(1, "d")
                        .format()
                    : moment().format();
                  this.prescriptionForm.get("startDate").setValue(newDate);
                  this.createPrescription(documents);
                } else {
                  this.isLoading = false;
                }
              });
            }
          }
        );
    }
  }

  private editPrescription(documents: IDocument[] = []) {
    const prescriptionEdited = Tools.deepCopy(this.patientRegistrationService.prescription);
    const prescriptionConfigEdited = Tools.deepCopy(this.patientRegistrationService.prescriptionConfig);
    const formValue = this.prescriptionForm.getRawValue();

    const doctorIndex = prescriptionEdited.performer.findIndex((p) => p.type === "Practitioner");
    prescriptionEdited.performer[doctorIndex].reference = PRESCRIPTION_TYPE.HOME_CARE_MOROCCO
      ? FHIRHelper.getMainIdentifier(formValue.chargeDoctor)?.value ?? this.sessionService.isAdmin()
        ? this.sessionService.account.caremateIdentifier
        : undefined
      : FHIRHelper.getMainIdentifier(formValue.chargeDoctor).value;
    prescriptionEdited.performer[doctorIndex].reference = FHIRHelper.getMainIdentifier(formValue.chargeDoctor).label;
    const nurseIndex = prescriptionEdited.performer.findIndex((p) => p.type === "Nurse");
    if (nurseIndex) {
      if (formValue.chargeNurse) {
        prescriptionEdited.performer[doctorIndex].reference = FHIRHelper.getMainIdentifier(formValue.chargeNurse).value;
        prescriptionEdited.performer[doctorIndex].reference = FHIRHelper.getMainIdentifier(formValue.chargeNurse).label;
      } else {
        prescriptionEdited.performer.splice(nurseIndex, 1);
      }
    } else if (formValue.chargeNurse) {
      prescriptionEdited.performer.push({
        type: "Nurse",
        reference: FHIRHelper.getMainIdentifier(formValue.chargeNurse).value,
        display: FHIRHelper.getMainIdentifier(formValue.chargeNurse).label,
      });
    }
    prescriptionEdited.occurence = {
      occurenceDateTime: formValue.startDate,
      occurenceTiming: formValue.prescriptionDuration
        ? {
            duration: formValue.prescriptionDuration,
            durationUnit: this.prescriptionPeriodTimeUnit.substring(0, 1),
          }
        : undefined,
      renewal: formValue.prescriptionDuration ? formValue.renewable : false,
      renewalParams: {
        duration:
          this.careplanPrescription.prescriptionType === PRESCRIPTION_TYPE.HOME_CARE_MOROCCO
            ? formValue.prescriptionDuration
            : formValue.renewDuration,
        durationUnit:
          this.careplanPrescription.prescriptionType === PRESCRIPTION_TYPE.HOME_CARE_MOROCCO
            ? this.prescriptionPeriodTimeUnit.substring(0, 1)
            : this.renewPeriodTimeUnit.substring(0, 1),
      },
    };
    prescriptionEdited.reason = this.linkedDocuments.concat(
      documents.map((d) => {
        return { reference: d.identifier, display: d.name };
      })
    );
    prescriptionConfigEdited.periodicity.frequency = formValue.isCumulative ? formValue.nbMonthCumulative : 1;
    prescriptionConfigEdited.careTeam.coefficient = formValue.coefficientIncrease;
    this.prescriptionService
      .editPrescription(prescriptionEdited, prescriptionConfigEdited)
      .pipe(first())
      .subscribe((result) => {
        this.patientRegistrationService.onPrescriptionCreated$.next(result);
        this.next();
        this.isLoading = false;
      });
  }

  public addTemplate(): void {
    const dialogRef = this.dialog.open(PrescriptionTemplateModalComponent, {
      data: {
        service: this.serviceRef,
        templates: this.practitionerTemplates,
        doctorSelected: this.prescriptionForm.get("chargeDoctor")?.value,
        availableDoctors: this.availableDoctors,
      },
      width: "700px",
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.templatesSelected = result;
        result.forEach((t) => {
          const isNewTemplate =
            this.practitionerTemplates.findIndex((practitionerTemplate) => practitionerTemplate.identifier === t.identifier) === -1;
          if (isNewTemplate) {
            this.practitionerTemplates.push(...result);
          }
        });
      }
    });
  }

  public onDeleteDocument(index: number, context?: string): void {
    if (context === "template") {
      this.templatesSelected.splice(index, 1);
    } else if (context === "linked") {
      this.linkedDocuments.splice(index, 1);
    } else {
      this.documents.splice(index, 1);
    }
  }

  public onDoctorChange(): void {
    const isCurrentPractitioner =
      this.prescriptionForm.get("chargeDoctor").value?.caremateId?.value === this.sessionService.account.caremateIdentifier;
    if (isCurrentPractitioner) {
      this.prescriptionForm.get("needValidation").setValue(false);
    }
    this.showNeedValidationCheckbox = !isCurrentPractitioner;
    this.checkCanSkip();
    this.setDefaultTemplate();
  }

  private setDefaultTemplate() {
    this.templatesSelected = [];
    const doctorSelected = this.prescriptionForm.get("chargeDocter")?.value;
    let defaultTemplate: IPractitionerTemplate[];
    if (doctorSelected) {
      defaultTemplate = this.practitionerTemplates.filter((t) => t.practitioner.reference === doctorSelected.caremateId.value);
    } else {
      defaultTemplate = this.practitionerTemplates.filter((t) => t.created_by === this.sessionService.account.caremateIdentifier);
    }
    if (defaultTemplate.length) {
      this.templatesSelected = defaultTemplate;
    }
  }

  private initFormWithPrescription(p: IPrescription): void {
    this.prescriptionForm.patchValue({
      isRenew: true,
      prescriptionDuration: p.occurence.occurenceTiming.duration,
      renewable: p.occurence.renewal,
      renewDuration: p.occurence.renewalParams.duration,
      chargeNurse: this.availableNurses.find(
        (nurse) => FHIRHelper.getMainIdentifier(nurse)?.value === p.performer.find((p) => p.type === "Nurse")?.reference
      ),
      chargeDoctor: this.availableDoctors.find(
        (doctor) => FHIRHelper.getMainIdentifier(doctor)?.value === p.performer.find((p) => p.type === "Practitioner")?.reference
      ),
      isCumulative: this.patientRegistrationService.prescriptionConfig?.periodicity?.frequency > 1,
      nbMonthCumulative: this.patientRegistrationService.prescriptionConfig?.periodicity?.frequency ?? undefined,
      coefficientIncrease: this.patientRegistrationService.prescriptionConfig?.careTeam.coefficient,
    });

    if (this.patientRegistrationService.isPendingPrescriptionEdit) {
      this.prescriptionForm.patchValue({
        startDate: p.occurence.occurenceDateTime,
        needValidation: true,
      });
      this.linkedDocuments = this.patientRegistrationService.prescription.reason;
    }

    this.onIsRenewChange();
    this.setEndDate(p.occurence.renewalParams.duration);
    this.onDoctorChange();
    return;
  }

  public showAutoCompletedTemplate(t: IPractitionerTemplate): void {
    this.prescriptionService
      .autoCompletePrescriptionTemplate(
        t.identifier,
        FHIRHelper.getMainIdentifier(this.patientRegistrationService.patientUser.patient).value,
        this.sessionService.userLang,
        this.prescriptionDuration
      )
      .pipe(first())
      .subscribe((blob) => {
        Tools.openBlob(blob);
      });
  }

  public get prescriptionDuration(): number {
    return this.prescriptionForm.get("prescriptionDuration")?.value;
  }

  private get pricingIndex(): number {
    return this.prescriptionForm.get("pricingIndex")?.value;
  }

  public onNoEndDateChange(event): void {
    if (!event.checked) {
      this.prescriptionForm.get("prescriptionDuration").setValue(12);
      this.setEndDate();
      this.isEndlessPrescription = false;
    } else {
      this.prescriptionForm.get("prescriptionDuration").setValue(undefined);
      this.isEndlessPrescription = true;
    }
  }

  public preventCharacter(event: KeyboardEvent, characters: PREVENTCHARACTER[], formControlName?: string): void {
    // we need this timeout because the replacement must be after the change of the input value
    setTimeout(() => {
      const value = PreventCharacter.preventMultiple(event, characters);
      this.prescriptionForm.get(formControlName).setValue(value, { emitEvent: false });
    }, 0);
  }
}
